import {
  createDepositLimitCapByPeriod,
  editDepositLimitCapByPeriod,
  deleteDepositLimitCapByPeriod,
  getDepositLimitCaps,
} from '@services/queries'
import {
  DELETE_LIMIT_MESSAGE,
  DELETE_LIMIT_MESSAGE_FAILURE,
  CREATE_LIMIT_MESSAGE,
  CREATE_LIMIT_MESSAGE_FAILURE,
  UPDATE_LIMIT_MESSAGE,
  UPDATE_LIMIT_MESSAGE_FAILURE,
} from '@constants'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { store } from 'react-notifications-component'
import { capitalize } from '@utils'

const DEFAULT_TYPE = 'deposit cap'

export function useDepositLimitCaps(
  networkId,
  currencyCode,
  type,
  enabled = true,
) {
  const info = useQuery(
    ['deposit_limit_cap', networkId, currencyCode, type],
    () => getDepositLimitCaps(networkId, currencyCode, type),
    {
      enabled: !!enabled && !!networkId && !!currencyCode,
    },
  )

  return info
}

export function useNewDepositLimitCapByPeriodMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  const mutationInfo = useMutation(
    (caps) => {
      if (Array.isArray(caps)) {
        return Promise.all(
          caps.map((cap) =>
            createDepositLimitCapByPeriod(
              cap.limitObject,
              cap.networkId,
              cap.period,
              cap.playerId,
              cap.type,
            ),
          ),
        )
      }

      return createDepositLimitCapByPeriod(
        caps.limitObject,
        caps.networkId,
        caps.period,
        caps.playerId,
        caps.type,
      )
    },
    {
      onSuccess(result, caps) {
        const invalidate = (networkId, currencyCode) =>
          queryClient.invalidateQueries([
            'deposit_limit_cap',
            networkId,
            currencyCode,
          ])

        if (Array.isArray(caps)) {
          let hasSuccess = false
          const type = caps[0]?.type || DEFAULT_TYPE

          caps.forEach((cap, idx) => {
            const res = result[idx]
            const data = res?.data
            const response = res?.response
            invalidate(cap.networkId, cap.currencyCode)
            if (response?.data?.error?.description) {
              throw response?.data?.error?.description
            }
            if (onSuccess) onSuccess(data)
            if (data?.result) {
              hasSuccess = true
            }
          })

          if (hasSuccess) {
            store.addNotification(CREATE_LIMIT_MESSAGE(type))
          }
        } else {
          const data = result?.data
          const response = result?.response
          invalidate(caps.networkId, caps.currencyCode)
          if (response?.data?.error?.description) {
            throw response?.data?.error?.description
          }
          if (onSuccess) onSuccess(data)
          if (data?.result) {
            store.addNotification(
              CREATE_LIMIT_MESSAGE(caps.type || DEFAULT_TYPE),
            )
          }
        }
      },
      onError(err, caps) {
        console.error('Error creating deposit limit caps', err)
        const type = Array.isArray(caps)
          ? caps[0]?.type || DEFAULT_TYPE
          : caps.type || DEFAULT_TYPE
        store.addNotification(CREATE_LIMIT_MESSAGE_FAILURE(type))
        if (onError && typeof err === 'string') onError(capitalize(err))
      },
    },
  )

  return mutationInfo
}

export function useEditDepositLimitCapByPeriodMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  const mutationInfo = useMutation(
    (caps) => {
      if (Array.isArray(caps)) {
        return Promise.all(
          caps.map((cap) =>
            editDepositLimitCapByPeriod(
              cap.limitObject,
              cap.networkId,
              cap.period,
              cap.currencyCode,
              cap.playerId,
              cap.type,
            ),
          ),
        )
      }

      return editDepositLimitCapByPeriod(
        caps.limitObject,
        caps.networkId,
        caps.period,
        caps.currencyCode,
        caps.playerId,
        caps.type,
      )
    },
    {
      onSuccess(result, caps) {
        const invalidate = (networkId, currencyCode) =>
          queryClient.invalidateQueries([
            'deposit_limit_cap',
            networkId,
            currencyCode,
          ])

        if (Array.isArray(caps)) {
          let hasSuccess = false
          const type = caps[0]?.type || DEFAULT_TYPE

          caps.forEach((cap, idx) => {
            const res = result[idx]
            const data = res?.data
            const response = res?.response
            invalidate(cap.networkId, cap.currencyCode)
            if (response?.data?.error?.description) {
              throw response?.data?.error?.description
            }
            if (onSuccess) onSuccess(data)
            if (data?.result) {
              hasSuccess = true
            }
          })

          if (hasSuccess) {
            store.addNotification(UPDATE_LIMIT_MESSAGE(type, false))
          }
        } else {
          const data = result?.data
          const response = result?.response
          invalidate(caps.networkId, caps.currencyCode)
          if (response?.data?.error?.description) {
            throw response?.data?.error?.description
          }
          if (onSuccess) onSuccess(data)
          const type = caps.type || DEFAULT_TYPE
          if (data?.result) {
            store.addNotification(UPDATE_LIMIT_MESSAGE(type, false))
          }
        }
      },
      onError(err, caps) {
        console.error('Error updating deposit limit caps', err)
        const type = Array.isArray(caps)
          ? caps[0]?.type || DEFAULT_TYPE
          : caps.type || DEFAULT_TYPE
        store.addNotification(UPDATE_LIMIT_MESSAGE_FAILURE(type))
        if (onError && typeof err === 'string') onError(capitalize(err))
      },
    },
  )

  return mutationInfo
}

export function useDeleteDepositLimitCapByPeriodMutation(onSuccess, onError) {
  const queryClient = useQueryClient()

  const mutationInfo = useMutation(
    (caps) => {
      if (Array.isArray(caps)) {
        return Promise.all(
          caps.map((cap) =>
            deleteDepositLimitCapByPeriod(
              cap.networkId,
              cap.period,
              cap.currencyCode,
              cap.playerId,
              cap.type,
            ),
          ),
        )
      }

      return deleteDepositLimitCapByPeriod(
        caps.networkId,
        caps.period,
        caps.currencyCode,
        caps.playerId,
        caps.type,
      )
    },
    {
      onSuccess(data, caps) {
        const invalidate = (networkId, currencyCode) =>
          queryClient.invalidateQueries([
            'deposit_limit_cap',
            networkId,
            currencyCode,
          ])

        if (Array.isArray(caps)) {
          caps.forEach((cap) => {
            invalidate(cap.networkId, cap.currencyCode)
          })
          if (onSuccess) onSuccess(data)
          const type = caps[0]?.type || DEFAULT_TYPE
          store.addNotification(DELETE_LIMIT_MESSAGE(type))
        } else {
          invalidate(caps.networkId, caps.currencyCode)
          if (onSuccess) onSuccess(data)
          const type = caps.type || DEFAULT_TYPE
          store.addNotification(DELETE_LIMIT_MESSAGE(type))
        }
      },
      onError(err, caps) {
        console.error('Error deleting deposit limit cap', err)
        const type = Array.isArray(caps)
          ? caps[0]?.type || DEFAULT_TYPE
          : caps?.type || DEFAULT_TYPE
        store.addNotification(DELETE_LIMIT_MESSAGE_FAILURE(type))
        if (onError) onError(err)
      },
    },
  )

  return mutationInfo
}
